import { useSalePrefix } from '../lib/listingPaths';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { money, shortDate } from '../lib/format';
import {
  ButtonLink, Card, EmptyState, ErrorBlock, LoadingBlock, StatusPill,
} from '../components/ui';

/**
 * A seller's own properties for sale.
 *
 * The screen's job is to answer "why is this not live yet?", because a sale
 * listing can be held up by two different things and the seller can only act
 * on one of them at a time. So each row says which gate is shut in words,
 * rather than showing a status the seller has to interpret.
 */

interface Row {
  id: string;
  title: string;
  asking_price: string;
  county: string | null;
  estate_area: string | null;
  status: string;
  reviewed_at: string | null;
  paid_until: string | null;
  published_at: string | null;
  rejection_reason: string | null;
  enquiry_count: number;
  photo_count: number;
}

const TONE: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  draft: 'neutral', pending_review: 'info', approved: 'warning', published: 'success',
  reserved: 'warning', sold: 'neutral', rejected: 'danger', expired: 'neutral',
};

/**
 * What the state actually means for the seller, and what to do about it.
 * `approved` is the one that needs saying out loud: the listing has passed
 * review and is waiting for money, which no single word conveys.
 */
function meaning(r: Row): string {
  const paid = r.paid_until !== null && new Date(r.paid_until).getTime() > Date.now();
  switch (r.status) {
    case 'draft': return 'Not submitted yet.';
    case 'pending_review': return 'With our team for review.';
    case 'approved': return paid
      ? 'Approved and paid — going live shortly.'
      : 'Approved. Pay for a publication slot to put it live.';
    case 'published': return paid
      ? `Live on Buy Property until ${shortDate(r.paid_until!)}.`
      : 'The publication slot has run out. Renew it to go back on.';
    case 'reserved': return 'Under offer. Still readable, no longer taking enquiries.';
    case 'sold': return 'Sold. The page stays up so buyers who saw it know.';
    case 'rejected': return 'Needs a change before it can go live.';
    case 'expired': return 'The publication slot ran out.';
    default: return '';
  }
}

/** Approved or lapsed, and nothing currently paying for it to be on the site. */
function needsSlot(r: Row): boolean {
  const paid = r.paid_until !== null && new Date(r.paid_until).getTime() > Date.now();
  return !paid && ['approved', 'published', 'expired'].includes(r.status);
}

export function MySaleListings() {
  const salePrefix = useSalePrefix();
  const newEnq = useApi<{ enquiries: { id: string }[] }>('/api/v1/sale-enquiries', { status: 'new' });
  const newCount = newEnq.data?.enquiries.length ?? 0;
  const { data, error, loading, reload } = useApi<{ listings: Row[] }>('/api/v1/sale-listings');
  const rows = data?.listings ?? [];

  const live = rows.filter((r) => r.status === 'published').length;
  const waiting = rows.filter((r) => r.status === 'approved').length;
  const enquiries = rows.reduce((n, r) => n + r.enquiry_count, 0);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Properties for sale</h1>
          <p className="page-sub">
            What you are advertising to buyers.{' '}
            {/* Only worth a word here: somebody with one property to sell does
                not need a package, and the page says so if they look. */}
            <Link to="/sale-packages">Selling several?</Link>{' · '}
            <Link to="/sale-demand">How are they doing?</Link>{' · '}
            <Link to="/sale-enquiries">
              Buyer enquiries{newCount ? ` (${newCount} new)` : ''}
            </Link>
          </p>
        </div>
        <ButtonLink to={`${salePrefix}/new`}>List a property</ButtonLink>
      </div>

      {loading ? <LoadingBlock />
        : error ? <Card><ErrorBlock error={error} onRetry={reload} /></Card>
        : rows.length === 0
          ? (
            <Card>
              <EmptyState
                title="Nothing listed for sale yet"
                message="Advertise a house, an apartment or a plot to buyers across Kenya. Preparing a listing is free; you pay to publish it once it has been reviewed."
                action={<ButtonLink to={`${salePrefix}/new`}>List a property</ButtonLink>}
              />
            </Card>
          )
          : (
            <>
              <div className="grid grid-kpi" style={{ marginBottom: 24 }}>
                <div className="kpi"><span className="kpi-label">Listed</span>
                  <span className="kpi-value">{rows.length}</span></div>
                <div className="kpi"><span className="kpi-label">Live now</span>
                  <span className="kpi-value">{live}</span></div>
                <div className="kpi"><span className="kpi-label">Waiting on payment</span>
                  <span className="kpi-value">{waiting}</span></div>
                <Link className="kpi" to="/sale-enquiries" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <span className="kpi-label">Enquiries</span>
                  <span className="kpi-value">{enquiries}</span>
                  {newCount > 0 && <span className="kpi-sub">{newCount} new, open them</span>}
                </Link>
              </div>

              <Card className="" bodyClassName="card-pad-0">
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Property</th><th>Where</th><th className="num">Asking price</th>
                        <th>State</th><th className="num">Enquiries</th><th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            {r.title}
                            <br />
                            <span className="muted">
                              {r.photo_count} photo{r.photo_count === 1 ? '' : 's'}
                            </span>
                          </td>
                          <td>{[r.estate_area, r.county].filter(Boolean).join(', ') || '—'}</td>
                          <td className="num">{money(r.asking_price)}</td>
                          <td>
                            <StatusPill status={r.status} tone={TONE[r.status] ?? 'neutral'} />
                            <br />
                            <span className="muted">{meaning(r)}</span>
                            {r.rejection_reason && (
                              <>
                                <br />
                                <span className="muted">“{r.rejection_reason}”</span>
                              </>
                            )}
                          </td>
                          <td className="num">{r.enquiry_count}</td>
                          <td>
                            <Link to={`${salePrefix}/${r.id}`}>Manage</Link>
                            {needsSlot(r) && (
                              <>
                                {' · '}
                                <Link to={`${salePrefix}/${r.id}/pay`}>
                                  {r.status === 'approved' ? 'Publish' : 'Renew'}
                                </Link>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
    </div>
  );
}
